<template>
  <div>
    <template v-if="ready">
      <div class="d-flex justify-content-between align-items-center px-2">
        <h5 class="mb-2">{{ $t("risk.select_form") }}</h5>

        <b-button
          variant="secondary"
          to="/risk/templates/builder"
          target="_blank"
          v-if="$can('create', 'RiskTemplate')"
        >
          <span class="text-nowrap text-capitalize">
            {{ $t("actions.add") + " Template" }}
          </span>
        </b-button>
      </div>

      <!-- Selecto fo multiple templates -->
      <div class="m-2">
        <v-select
          v-model="itemData.risk_templates"
          :options="filteredTemplates"
          :reduce="(t) => t.id"
          label="label"
          :placeholder="$t('risk.select_form_place_holder')"
          multiple
        />
      </div>

      <!-- Preview and button answer form” -->
      <div class="mt-2" v-if="selectedTemplates.length">
        <div class="d-flex justify-content-between align-items-center">
          <h5>{{ $t("risk.selected_form") }}</h5>

          <b-button
            variant="secondary"
            @click="onAnswerClick"
            :disabled="!mergedSnapshot.length || answering"
          >
            {{
              answering
                ? $t("loading") || "Cargando..."
                : $t("risk.answer_form") || "Responder formulario"
            }}
          </b-button>
        </div>

        <!-- Preview questions -->
        <div class="mt-2">
          <h6 class="mt-1">{{ $tc("risk.template.question", 2) }}</h6>

          <div v-if="!mergedSnapshot.length" class="text-muted small">
            {{ $t("loading") }}...
          </div>

          <ul v-else>
            <li v-for="(q, idx) in previewQuestions" :key="q.item_id || idx" class="mb-2">
              <p class="mb-1">
                <strong>{{ $tc("risk.template.question", 1)+ "-" + (idx + 1) }}:</strong>
                <span class="text-muted">
                  [{{ q.section || $t("risk.template.no_section") }}]
                </span>
                {{ q.name }}
              </p>
              <p class="mb-1">
                <strong>{{ $tc("custom_forms.types.title", 2) }}:</strong>
                {{ q.type }}
              </p>

              <template
                v-if="q.options && q.options.fieldTypeCfg && q.options.fieldTypeCfg.data && q.options.fieldTypeCfg.data.length"
              >
                <p class="mb-1">
                  <strong>{{ $t("risk.template.options") }}:</strong>
                </p>
                <ul class="mb-0">
                  <li v-for="(opt, oIdx) in q.options.fieldTypeCfg.data" :key="oIdx">
                    {{ opt.name }}
                    <span v-if="typeof opt.riskPercentage !== 'undefined'">
                      ({{ opt.riskPercentage }}%)
                    </span>
                  </li>
                </ul>
              </template>
            </li>
          </ul>
        </div>
      </div>
    </template>

    <div class="text-center" v-else>
      <b-spinner label="Spinning" variant="primary"></b-spinner>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "@vue/composition-api";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import { shouldUpdateExistingRecordToInProgress } from "@/libs/risk/scheduledStartFormAccess";
import vSelect from "vue-select";
import { BButton, BSpinner } from "bootstrap-vue";

export default {
  name: "RiskAssessmentStep",
  components: { vSelect, BButton, BSpinner },
  props: {
    propItemData: { type: Object, required: true },
    existingRiskEvaluationId: { type: Number, default: null },
    existingRecordLastStatusName: { type: String, default: null },
  },
  setup(props, { emit }) {
    const toast = useToast();

    //local state
    const itemData = ref({
      ...props.propItemData,
      risk_templates: Array.isArray(props.propItemData?.risk_templates)
        ? props.propItemData.risk_templates.slice()
        : [],
      reference_number: props.propItemData?.reference_number || `RE-${Date.now()}`,
    });

    const ready = ref(false);
    const answering = ref(false);

    const riskTemplates = ref([]);      // catalog
    const mergedSnapshot = ref([]);     // snapshots merged

    //Auto pick default template
    //const DEFAULT_TEMPLATE_NAME_MATCH = /^\s*Cuestionario\s+General\s*$/i; //exact  name "Cuestionario General"
    const DEFAULT_TEMPLATE_NAME_MATCH = /cuestionario\s+general/i; //can have Cuestionario General v2, etc
    const didAutoPickDefault = ref(false);

    // Charges templates
    const fetchTemplates = async () => {
      try {
        const { data } = await axios.get("/v1/risk_templates");
        riskTemplates.value = data.data || [];
      } catch (e) {
        console.error("Error fetching templates:", e);
        toast.error("Error al cargar los templates");
      }
    };

    const filteredTemplates = computed(() => {
      const tenantId = itemData.value.id_tenant;
      const list = riskTemplates.value || [];
      const byTenant = list.filter((t) => Number(t.id_tenant) === Number(tenantId));
      const source = byTenant.length ? byTenant : list;
      return source.map((t) => ({ id: t.id, label: t.name }));
    });

    const selectedTemplates = computed(() => {
      const selIds = new Set(itemData.value.risk_templates || []);
      const byId = new Map((riskTemplates.value || []).map((t) => [t.id, t]));
      return [...selIds].map((id) => byId.get(id)).filter(Boolean);
    });

    const applyDefaultTemplateSelection = () => {
      if (didAutoPickDefault.value) return;
      const current = itemData.value.risk_templates || [];
      if (Array.isArray(current) && current.length) return;

      const opts = filteredTemplates.value || [];
      if (!opts.length) return;

      const hit = opts.find((o) => DEFAULT_TEMPLATE_NAME_MATCH.test(String(o.label || "")));
      if (!hit) return;

      // v-select is multiple, so  we set array
      itemData.value.risk_templates = [hit.id];
      didAutoPickDefault.value = true;
    };

    // Template merge to a snpashot
    const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

    const namespaceFieldIds = (tplId, fields) => {
      const list = Array.isArray(fields) ? fields : [];
      return list.map((f) => {
        const nf = deepClone(f);
        const oldId = nf.item_id;
        nf.item_id = `${tplId}:${oldId}`;
        if (Array.isArray(nf.show_if)) {
          nf.show_if = nf.show_if.map((c) =>
            c && c.field ? { ...c, field: `${tplId}:${c.field}` } : c
          );
        }
        return nf;
      });
    };

    const mergeTemplatesToSnapshot = (templates) => {
      const out = [];
      (templates || []).forEach((tpl) => {
        const data = Array.isArray(tpl.data) ? tpl.data : [];
        namespaceFieldIds(tpl.id, data).forEach((f) => out.push(f));
      });
      return out;
    };

    const recomputeMergedSnapshot = () => {
      const sels = selectedTemplates.value;
      mergedSnapshot.value = sels.length ? mergeTemplatesToSnapshot(sels) : [];
    };

    watch(
      () => itemData.value.risk_templates,
      (nv, ov) => { if (nv !== ov) recomputeMergedSnapshot(); }
    );

    //if options change (tenant/unit changed), try default once
    watch(
      () => filteredTemplates.value,
      () => { applyDefaultTemplateSelection(); },
      { deep: true }
    );

    // Checkers and creation
    const findInProgressEvaluation = async (unitId) => {
      const resp = await axios.get("/v1/risk_evaluations", {
        params: { id_unit: unitId, with: ["last_status"] },
      });
      const payload = resp && resp.data ? resp.data : resp;
      let list = [];
      if (Array.isArray(payload)) list = payload;
      else if (Array.isArray(payload?.data)) list = payload.data;
      else if (Array.isArray(payload?.data?.data)) list = payload.data.data;
      else if (payload && typeof payload?.data === 'object' && payload.data) list = Object.values(payload.data).filter(Boolean);
      if (!Array.isArray(list)) list = [];
      const ev = list.find((x) => x?.last_status?.name === "in_progress");
      return ev || null;
    };

    const showAlreadyInProgress = (ev) => {
      const ref = ev?.reference_number || ev?.reference || ev?.id;
      toast.error(`Ya existe una evaluación en progreso para esta instalación (Ref: ${ref}). Ábrela para continuar.`);
    };

    const createEvaluation = async () => {
      const payload = {
        reference_number: itemData.value.reference_number,
        id_unit: itemData.value.id,
        evaluation_date: new Date().toISOString(),
      };
      const resp = await axios.post("/v1/risk_evaluations", payload);
      return resp.data?.data?.id;
    };

    const resolveRiskEvaluationId = async () => {
      if (props.existingRiskEvaluationId != null && Number.isFinite(Number(props.existingRiskEvaluationId))) {
        return Number(props.existingRiskEvaluationId);
      }
      return createEvaluation();
    };

    const isExistingScheduledFlow = () =>
      props.existingRiskEvaluationId != null && Number.isFinite(Number(props.existingRiskEvaluationId));

    const updateStatusToInProgress = async (evaluationId) => {
      await axios.post(`/v1/risk_evaluations/update_status/${evaluationId}`, { status: "in_progress" });
    };

    const onAnswerClick = async () => {
      if (!selectedTemplates.value.length || !mergedSnapshot.value.length) return;
      answering.value = true;
      try {
        const unitId = itemData.value.id;

        if (!isExistingScheduledFlow()) {
          const existing = await findInProgressEvaluation(unitId);
          if (existing) {
            showAlreadyInProgress(existing);
            return;
          }
        }

        const evaluationId = await resolveRiskEvaluationId();
        if (!evaluationId) throw new Error("No se pudo obtener la evaluación");
        if (
          isExistingScheduledFlow() &&
          shouldUpdateExistingRecordToInProgress(props.existingRecordLastStatusName)
        ) {
          await updateStatusToInProgress(evaluationId);
        }

        emit("open-evaluation-modal", {
          id: evaluationId,
          snapshot: mergedSnapshot.value,
          sources: (itemData.value.risk_templates || []).slice(),
        });
        emit("assessment-complete");
      } catch (e) {
        console.error(e);
        const msg = e?.response?.data?.message || "No se pudo crear la evaluación";
        toast.error(msg);
      } finally {
        answering.value = false;
      }
    };

    const previewQuestions = computed(() => mergedSnapshot.value);

    onMounted(async () => {
      await fetchTemplates();
      applyDefaultTemplateSelection();
      ready.value = true;
    });

    return {
      // state/UI
      ready,
      answering,

      // data
      itemData,
      filteredTemplates,
      selectedTemplates,
      mergedSnapshot,
      previewQuestions,

      // actions
      onAnswerClick,
    };
  },
};
</script>
